import React, { useEffect, useMemo, useState } from 'react';
import { Container, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiOutlineSearch } from 'react-icons/hi';
import { BsFileEarmarkText } from 'react-icons/bs';
import { AiOutlineEye } from 'react-icons/ai';
import { FiDownload } from 'react-icons/fi';
import Head from 'components/metatags_helmet/metatags';
import TitleWithLine from 'components/titleWithLine';
import 'assets/css/components/orderDocumentsPage.scss';

const DocumentSection = ({ title, documents, emptyText, viewLabel, downloadLabel }) => (
  <section>
    <h3 className="order-documents-page__section-title">{title}</h3>
    {documents.length === 0 ? (
      <p className="order-documents-page__empty">{emptyText}</p>
    ) : (
      documents.map((doc) => (
        <div key={doc.id} className="order-documents-page__doc">
          <div className="order-documents-page__doc-info">
            <BsFileEarmarkText className="order-documents-page__doc-icon" aria-hidden />
            <div className="min-w-0">
              <p className="order-documents-page__doc-name">{doc.name}</p>
              <p className="order-documents-page__doc-meta">{doc.meta}</p>
            </div>
          </div>
          <div className="order-documents-page__doc-actions">
            <button type="button" className="order-documents-page__action-btn">
              <AiOutlineEye />
              <span>{viewLabel}</span>
            </button>
            <button type="button" className="order-documents-page__action-btn">
              <FiDownload />
              <span>{downloadLabel}</span>
            </button>
          </div>
        </div>
      ))
    )}
  </section>
);

const OrderDocumentsPage = () => {
  const { t } = useTranslation(['header', 'orderDocuments', 'backoffice']);
  const title = t('albaranesYFacturas', { ns: 'header' });
  const [search, setSearch] = useState('');
  const [orders] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return orders;
    return orders.filter((order) => order.date?.toLowerCase().includes(query));
  }, [orders, search]);

  return (
    <Container fluid className="margin-top-md px-4 mainDiv order-documents-page-wrapper">
      <Head title={title} noIndex />
      <div className="order-documents-page">
        <div className="order-documents-page__header">
          <TitleWithLine text={title} />
        </div>

        <div className="order-documents-page__search">
          <HiOutlineSearch className="order-documents-page__search-icon" aria-hidden />
          <Form.Control
            type="search"
            className="order-documents-page__search-input"
            placeholder={t('searchByDate', { ns: 'orderDocuments' })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t('searchByDate', { ns: 'orderDocuments' })}
          />
        </div>

        <div className="order-documents-page__list">
          {filteredOrders.length === 0 ? (
            <p className="order-documents-page__empty-list">
              {t('noFilesAvailable', { ns: 'orderDocuments' })}
            </p>
          ) : (
            filteredOrders.map((order) => (
              <article key={order.id} className="order-documents-page__card">
                <header className="order-documents-page__card-header">
                  <p className="order-documents-page__card-date">{order.date}</p>
                  <div className="order-documents-page__card-meta">
                    <span className="order-documents-page__status-badge">{order.status}</span>
                    <p className="order-documents-page__card-total">{order.total}</p>
                  </div>
                </header>
                <div className="order-documents-page__card-body">
                  <DocumentSection
                    title={t('albaranes', { ns: 'orderDocuments' })}
                    documents={order.albaranes ?? []}
                    emptyText={t('noAlbaranesYet', { ns: 'orderDocuments' })}
                    viewLabel={t('orderTrans.viewDocument', { ns: 'backoffice' })}
                    downloadLabel={t('orderTrans.downloadDocument', { ns: 'backoffice' })}
                  />
                  <DocumentSection
                    title={t('facturas', { ns: 'orderDocuments' })}
                    documents={order.facturas ?? []}
                    emptyText={t('noFacturasYet', { ns: 'orderDocuments' })}
                    viewLabel={t('orderTrans.viewDocument', { ns: 'backoffice' })}
                    downloadLabel={t('orderTrans.downloadDocument', { ns: 'backoffice' })}
                  />
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </Container>
  );
};

export default OrderDocumentsPage;
